import type { VehicleIssueFilter } from "@/domains/vehicles/vehicleComplianceStatus";
import type { VehicleRucFilter } from "@/domains/vehicles/vehicleRucStatus";
import { defineStore } from "pinia";
import { ref } from "vue";

export type DashboardDialogContent =
  | "notifications"
  | "account"
  | "account-edit"
  | "my-fleet"
  | "fleet-member"
  | "fleet-locations"
  | "fleet-location-details"
  | "fleet-vehicles"
  | "vehicle-details"
  | "vehicle-edit-details"
  | "saved-trips"
  | "saved-trip-details";

export const defaultDisplayedContent: DashboardDialogContent = "my-fleet";

export const useDashboardNavStore = defineStore("dashboardNav", () => {
  // Dashboard navigation state.

  const displayedContent = ref<DashboardDialogContent>(defaultDisplayedContent);

  const showDashboard = ref<boolean>(true);

  // Context for the currently displayed dashboard content.

  const displayedVehicleId = ref<string | null>(null);
  const displayedTripId = ref<string | null>(null);
  const displayedUserId = ref<string | null>(null);
  const displayedLocationId = ref<string | null>(null);

  /**
   * Issue filter to seed VehicleList.vue's filtering with the next time it
   * displays, e.g. when navigating there from a clicked
   * VehicleIssuesSummaryCard.vue stat. Consumed (and cleared) by VehicleList
   * on read rather than kept in sync - it's a one-shot handoff, not a
   * permanent binding, so the user can freely change/clear the filter once
   * there without fighting this store.
   */
  const pendingVehicleListIssueFilter = ref<
    VehicleIssueFilter | VehicleRucFilter | null
  >(null);

  /**
   * Local vehicle ids to seed VehicleList.vue's display with the next time
   * it displays, e.g. when navigating there from a notification about
   * several vehicles at once. Same one-shot handoff contract as
   * `pendingVehicleListIssueFilter` above - consumed and cleared by
   * VehicleList on read.
   */
  const pendingVehicleListVehicleIds = ref<string[] | null>(null);

  // actions to update the dashboard navigation state

  function closeDashboard() {
    showDashboard.value = false;
  }

  function showVehicleList(
    issueFilter?: VehicleIssueFilter | VehicleRucFilter,
    vehicleIds?: string[],
  ) {
    displayedTripId.value = null;
    displayedUserId.value = null;
    displayedLocationId.value = null;
    displayedVehicleId.value = null;

    pendingVehicleListIssueFilter.value = issueFilter ?? null;
    pendingVehicleListVehicleIds.value = vehicleIds ?? null;

    displayedContent.value = "fleet-vehicles";
    showDashboard.value = true;
  }

  function showVehicleDetails(vehicleId: string) {
    displayedTripId.value = null;
    displayedUserId.value = null;
    displayedLocationId.value = null;

    displayedVehicleId.value = vehicleId;

    displayedContent.value = "vehicle-details";
    showDashboard.value = true;
  }

  function showLocationDetails(locationId: string) {
    displayedTripId.value = null;
    displayedUserId.value = null;
    displayedVehicleId.value = null;

    displayedLocationId.value = locationId;

    displayedContent.value = "fleet-location-details";
    showDashboard.value = true;
  }

  function showTripDetails(tripId: string) {
    displayedUserId.value = null;
    displayedVehicleId.value = null;
    displayedLocationId.value = null;
    
    displayedTripId.value = tripId;

    displayedContent.value = "saved-trip-details";
    showDashboard.value = true;
  }

    function showMemberDetails(userId: string) {
    displayedTripId.value = null;
    displayedLocationId.value = null;
    displayedVehicleId.value = null;
    
    displayedUserId.value = userId;

    displayedContent.value = "fleet-member";
    showDashboard.value = true;
  }

  return {
    /** The dashboard section currently being displayed. */
    displayedContent,
    /** Whether the dashboard dialog is currently open. */
    showDashboard,
    /** The vehicle currently being displayed by the dashboard, if applicable. */
    displayedVehicleId,
    /** The trip currently being displayed by the dashboard, if applicable. */
    displayedTripId,
    /** The user currently being displayed by the dashboard, if applicable. */
    displayedUserId,
    /** The location currently being displayed by the dashboard, if applicable. */
    displayedLocationId,
    /**
     * Issue filter to seed VehicleList.vue's filtering with the next time it
     * displays. One-shot - VehicleList consumes and clears this itself.
     */
    pendingVehicleListIssueFilter,
    /**
     * Vehicle ids to seed VehicleList.vue's display with the next time it
     * displays. One-shot - VehicleList consumes and clears this itself.
     */
    pendingVehicleListVehicleIds,
    /** Closes the dashboard dialog. */
    closeDashboard,
    /** Opens the dashboard showing the fleet vehicle list, optionally pre-filtered to one issue and/or narrowed to a set of vehicle ids. */
    showVehicleList,
    /** Opens the dashboard displaying the specified vehicles details. */
    showVehicleDetails,
    /** Opens the dashboard displaying the specified locations details. */
    showLocationDetails,
    /** Opens the dashboard displaying the specified trip details. */
    showTripDetails,
    /** Opens the dashboard displaying the specified fleet members details. */
    showMemberDetails,
  };
});
